/**
 * AdminGoldenCases — curated demo/reference cases (admin dashboard §1/§13).
 * Lists golden cases (not tied to a firm) with posture badges, status chips,
 * event date, claim amount, and the per-case seat lens (reuses ViewAsMenu).
 * Consumes GET /api/v1/admin/golden-cases.
 *
 * Coded by kimi 2.6 to a conductor-verified spec; conductor-reviewed + applied.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Star } from 'lucide-react';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';
import AppLayout from '../../components/AppLayout';
import ViewAsMenu from '../../components/admin/ViewAsMenu';

interface GoldenCase {
  id: string;
  name: string;
  posture?: 'first_party' | 'third_party' | null;
  status?: string;
  event_date?: string | null;
  created_at?: string | null;
  claim_amount?: number | null;
}

interface GoldenCasesResponse {
  golden_cases: GoldenCase[];
  total: number;
}

type PostureFilter = 'all' | 'first_party' | 'third_party' | 'unspecified';

const postureLabel = (p?: string | null) => {
  if (p === 'first_party') return 'First-party';
  if (p === 'third_party') return 'Third-party';
  return 'Unspecified';
};

const fmtMoney = (n?: number | null) => {
  if (n === null || n === undefined || n === 0) return '—';
  return `$${n.toLocaleString()}`;
};

const fmtDate = (d?: string | null) => {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function AdminGoldenCases() {
  const { getToken } = useAuth();
  const [cases, setCases] = useState<GoldenCase[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<PostureFilter>('all');

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const res = await api.get<GoldenCasesResponse>('/api/v1/admin/golden-cases', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (active) {
          setCases(res?.golden_cases || []);
          setTotal(res?.total ?? 0);
        }
      } catch (e: any) {
        if (active) setError(e?.message || 'Could not load golden cases.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [getToken]);

  const filteredCases = useMemo(() => {
    if (filter === 'all') return cases;
    return cases.filter((c) => {
      if (filter === 'unspecified') return !c.posture;
      return c.posture === filter;
    });
  }, [cases, filter]);

  const filterOptions: { key: PostureFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'first_party', label: 'First-party' },
    { key: 'third_party', label: 'Third-party' },
    { key: 'unspecified', label: 'Unspecified' },
  ];

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto space-y-8 p-6">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-text-muted)' }} />
          </div>
        ) : error ? (
          <p className="py-10 text-center text-sm" style={{ color: 'var(--color-needs-human-text)' }}>{error}</p>
        ) : (
          <>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <Star className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                <h1 className="cs-h1" style={{ color: 'var(--color-text-primary)' }}>Golden Cases</h1>
              </div>
              <p className="cs-meta mt-1" style={{ color: 'var(--color-text-muted)' }}>
                Demo + reference cases — not tied to a firm. The seat lens works on them identically.
              </p>
            </div>

            {/* TODO: BI/first/third taxonomy may expand — this filter currently uses only the posture axis. */}
            <div className="flex flex-wrap gap-2">
              {filterOptions.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setFilter(opt.key)}
                  className="font-mono text-[10px] uppercase tracking-wide px-3 py-1.5 rounded transition-colors"
                  style={{
                    border: '1px solid var(--color-border)',
                    color: filter === opt.key ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
                    background: filter === opt.key ? 'var(--color-bg-elevated)' : 'transparent',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-surface)' }}>
              {filteredCases.length === 0 ? (
                <p className="py-12 text-center text-sm" style={{ color: 'var(--color-text-muted)' }}>
                  {cases.length === 0 ? 'No golden cases configured yet.' : 'No cases match this filter.'}
                </p>
              ) : (
                filteredCases.map((c, i) => {
                  const isThirdParty = c.posture === 'third_party';
                  return (
                    <div key={c.id} className="flex items-center gap-4 px-5 py-4"
                      style={{ borderTop: i ? '1px solid var(--color-border)' : 'none' }}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{c.name}</span>
                          <span className="font-mono text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded"
                            style={{
                              color: isThirdParty ? 'var(--color-needs-human-text)' : 'var(--color-text-muted)',
                              border: `1px solid ${isThirdParty ? 'var(--color-needs-human-text)' : 'var(--color-text-muted)'}`,
                            }}>
                            {postureLabel(c.posture)}
                          </span>
                          {c.status && (
                            <span className="font-mono text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded"
                              style={{ color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }}>
                              {c.status}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-xs font-mono" style={{ color: 'var(--color-text-dim)' }}>
                          {fmtDate(c.event_date || c.created_at)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-mono" style={{ color: 'var(--color-text-primary)' }}>{fmtMoney(c.claim_amount)}</div>
                      </div>
                      <div className="flex-shrink-0">
                        <ViewAsMenu caseId={c.id} />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
